import { useLocation, useNavigate } from 'react-router-dom';
import { Flex, Text, Skeleton } from '@backstage/ui';
import {
  Page,
  Header,
  HeaderTabs,
  Content,
  HeaderLabel,
  ErrorPage,
} from '@backstage/core-components';
import { GitHubRateLimitCard } from './GitHubRateLimitCard';
import { IncrementalProvidersCard } from './IncrementalProvidersCard';
import { BuildInfoCard } from './BuildInfoCard';
import { useDevToolsAuthorization } from './useDevToolsAuthorization';
import './devtools.css';

const TABS: Array<{ id: string; label: string; render: () => JSX.Element }> = [
  {
    id: 'build-info',
    label: 'Build Info',
    render: () => <BuildInfoCard />,
  },
  {
    id: 'github-rate-limit',
    label: 'GitHub Rate Limit',
    render: () => <GitHubRateLimitCard />,
  },
  {
    id: 'incremental-providers',
    label: 'Incremental Providers',
    render: () => <IncrementalProvidersCard />,
  },
];

export const DevToolsPage = () => {
  const { isLoading, isAuthorized, userRef } = useDevToolsAuthorization();
  const location = useLocation();
  const navigate = useNavigate();

  // The URL hash is the source of truth for which tab is active. Pages
  // without a hash, or with an unknown hash, fall back to the first tab so
  // that bookmarks like `/dev-tools` keep working unchanged.
  const hashTabId = location.hash.replace(/^#/, '');
  const matchedIndex = TABS.findIndex(tab => tab.id === hashTabId);
  const selectedIndex = matchedIndex >= 0 ? matchedIndex : 0;

  const handleTabChange = (newIndex: number) => {
    const nextTab = TABS[newIndex];
    if (!nextTab) return;
    // `replace` keeps tab switching out of the browser history stack — the
    // back button still goes to the previous page, not the previous tab.
    navigate({ hash: `#${nextTab.id}` }, { replace: true });
  };

  if (isLoading) {
    return (
      <Page themeId="tool">
        <Header title="Developer Tools" subtitle="Administrative tools for managing Homestead">
          <HeaderLabel label="Status" value="Beta" />
        </Header>
        <Content>
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="3"
            p="6"
          >
            <Skeleton width="200px" height={24} />
            <Text variant="title-small">Checking authorization...</Text>
          </Flex>
        </Content>
      </Page>
    );
  }

  if (!isAuthorized) {
    return (
      <Page themeId="tool">
        <Header title="Developer Tools" subtitle="Administrative tools for managing Homestead">
          <HeaderLabel label="Status" value="Beta" />
        </Header>
        <Content>
          <ErrorPage
            status="403"
            statusMessage="Access Denied"
            additionalInfo={
              <Flex direction="column" gap="2">
                <Text variant="body-medium">
                  You do not have permission to access the Developer Tools page.
                </Text>
                <Text variant="body-small" color="secondary">
                  Current user: {userRef}
                </Text>
                <Text variant="body-small" color="secondary">
                  This page is restricted to specific authorized users.
                </Text>
              </Flex>
            }
          />
        </Content>
      </Page>
    );
  }

  const activeTab = TABS[selectedIndex];

  return (
    <Page themeId="tool">
      <Header title="Developer Tools" subtitle="Administrative tools for managing Homestead">
        <HeaderLabel label="Status" value="Beta" />
        <HeaderLabel label="User" value={userRef || 'Unknown'} />
      </Header>
      <HeaderTabs
        selectedIndex={selectedIndex}
        onChange={handleTabChange}
        tabs={TABS.map(({ id, label }) => ({ id, label }))}
      />
      <Content>
        <div className="devtools-page">{activeTab.render()}</div>
      </Content>
    </Page>
  );
};
